import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Typography,
    Grid,
    Chip,
    Paper,
    Box,
    Divider,
} from "@mui/material";
import {
    Info,
    Close,
    HourglassEmpty,
    Send,
    DoneAll,
    Visibility as VisibilityIcon,
    ErrorOutline,
    Autorenew,
    StopCircle,
} from "@mui/icons-material";
import axiosInstance from "../../../api/axiosInstance";
import useNotification from "../../common/Notification";
import { blue, green, orange, red, purple, indigo } from "@mui/material/colors";
import { WB_SX, WB_UI } from "../../../ui/ux/uiTokens";

const CampaignStatusModal = ({ open, onClose, campaignName }) => {
    const [loading, setLoading] = useState(false);
    const [statusData, setStatusData] = useState(null);
    const showNotification = useNotification();

    const num = (v) => Number(v) || 0;
    const percent2 = (value, denom) => {
        if (!denom || denom <= 0) return null;
        return Math.round((value / denom) * 10000) / 100;
    };

    useEffect(() => {
        if (open && campaignName) {
            fetchCampaignStatus();
        } else {
            setStatusData(null);
        }
    }, [open, campaignName]);

    const fetchCampaignStatus = async () => {
        if (!campaignName) return;

        setLoading(true);
        try {
            const response = await axiosInstance.get(
                `/whatsapp-business/campaign/status?campaign_name=${encodeURIComponent(
                    campaignName
                )}`
            );
            if (response.data.success) {
                setStatusData(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching campaign status:", error);
            showNotification(
                error.response?.data?.message ||
                    "Failed to fetch campaign status",
                "error"
            );
            setStatusData(null);
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "running":
                return green[600];
            case "stopped":
                return red[600];
            case "pending":
                return orange[600];
            default:
                return blue[600];
        }
    };

    const getStatusCountColor = (status) => {
        const colors = {
            PENDING: orange[50],
            SENT: blue[50],
            DELIVERED: green[50],
            READ: purple[50],
            FAILED: red[50],
            AUTO_RETRYING: indigo[50],
            USER_STOPPED: red[50],
        };
        return colors[status] || WB_UI.color.softInfoBg;
    };

    const getStatusBorderColor = (status) => {
        const colors = {
            PENDING: orange[200],
            SENT: blue[200],
            DELIVERED: green[200],
            READ: purple[200],
            FAILED: red[200],
            AUTO_RETRYING: indigo[200],
            USER_STOPPED: red[200],
        };
        return colors[status] || WB_UI.color.border;
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    border: `1px solid ${WB_UI.color.border}`,
                    overflow: "hidden",
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: `1px solid ${WB_UI.color.border}`,
                    backgroundColor: WB_UI.color.softInfoBg,
                    py: 1.5,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1.5,
                            display: "grid",
                            placeItems: "center",
                            backgroundColor: "#fff",
                            border: `1px solid ${WB_UI.color.border}`,
                            color: WB_UI.color.primaryButtonBg,
                        }}
                    >
                        <Info fontSize="small" />
                    </Box>
                    <Typography variant="h6" sx={WB_SX.sectionTitle}>
                        Campaign Status
                    </Typography>
                </Box>
                <Button
                    onClick={onClose}
                    sx={{ minWidth: "auto", p: 0.5, color: "text.secondary" }}
                >
                    <Close />
                </Button>
            </DialogTitle>

            <DialogContent sx={{ mt: 2 }}>
                {loading ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: 200,
                        }}
                    >
                        <CircularProgress sx={{ color: WB_UI.color.primaryButtonBg }} />
                    </Box>
                ) : statusData ? (
                    <Box>
                        {(() => {
                            const counts = statusData.status_counts || {};
                            const sentAdjusted =
                                num(counts.SENT) + num(counts.DELIVERED) + num(counts.READ);
                            const deliveredAdjusted =
                                num(counts.DELIVERED) + num(counts.READ);
                            const total = num(statusData.total_messages);

                            const breakdown = [
                                {
                                    key: "PENDING",
                                    label: "Pending",
                                    value: num(counts.PENDING),
                                    denom: total,
                                },
                                {
                                    key: "SENT",
                                    label: "Sent",
                                    value: sentAdjusted,
                                    denom: total,
                                },
                                {
                                    key: "DELIVERED",
                                    label: "Delivered",
                                    value: deliveredAdjusted,
                                    denom: sentAdjusted,
                                },
                                {
                                    key: "READ",
                                    label: "Read",
                                    value: num(counts.READ),
                                    denom: total,
                                },
                                {
                                    key: "FAILED",
                                    label: "Failed",
                                    value: num(counts.FAILED),
                                    denom: total,
                                },
                                {
                                    key: "AUTO_RETRYING",
                                    label: "Auto Retrying",
                                    value: num(counts.AUTO_RETRYING),
                                    denom: total,
                                },
                                {
                                    key: "USER_STOPPED",
                                    label: "User Stopped",
                                    value: num(counts.USER_STOPPED),
                                    denom: total,
                                },
                            ];

                            const getStatusIcon = (key) => {
                                switch (key) {
                                    case "PENDING":
                                        return <HourglassEmpty fontSize="small" color="warning" />;
                                    case "SENT":
                                        return <Send fontSize="small" color="info" />;
                                    case "DELIVERED":
                                        return <DoneAll fontSize="small" color="success" />;
                                    case "READ":
                                        return <VisibilityIcon fontSize="small" sx={{ color: "#111827" }} />;
                                    case "FAILED":
                                        return <ErrorOutline fontSize="small" color="error" />;
                                    case "AUTO_RETRYING":
                                        return <Autorenew fontSize="small" color="warning" />;
                                    case "USER_STOPPED":
                                        return <StopCircle fontSize="small" color="error" />;
                                    default:
                                        return null;
                                }
                            };

                            return (
                                <>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            flexWrap: "wrap",
                                            gap: 1.5,
                                            mb: 2.5,
                                            p: 2,
                                            borderRadius: 2,
                                            backgroundColor: WB_UI.color.softInfoBg,
                                            border: `1px solid ${WB_UI.color.border}`,
                                        }}
                                    >
                                        <Typography variant="h6" sx={{ ...WB_SX.sectionTitle, mb: 0 }}>
                                            {statusData.campaign_name}
                                        </Typography>
                                        <Chip
                                            label={(statusData.status || "").toUpperCase()}
                                            sx={{
                                                backgroundColor: getStatusColor(statusData.status),
                                                color: "white",
                                                fontWeight: 700,
                                                fontSize: "0.75rem",
                                                height: 28,
                                                borderRadius: 1.5,
                                            }}
                                        />
                                    </Box>

                                    <Typography variant="subtitle2" sx={{ ...WB_SX.sectionTitle, mb: 1.5 }}>
                                        Status Breakdown
                                    </Typography>

                                    <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                                        {breakdown
                                            .filter(
                                                (s) =>
                                                    s.value > 0 ||
                                                    ["PENDING", "SENT", "DELIVERED", "READ"].includes(s.key)
                                            )
                                            .map((s) => {
                                                const p = percent2(s.value, s.denom);
                                                return (
                                                    <Grid item xs={12} sm={6} md={4} key={s.key}>
                                                        <Paper
                                                            elevation={0}
                                                            sx={{
                                                                p: 2,
                                                                textAlign: "center",
                                                                borderRadius: 2,
                                                                backgroundColor: getStatusCountColor(s.key),
                                                                border: `1px solid ${getStatusBorderColor(s.key)}`,
                                                                height: "100%",
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: "flex",
                                                                    justifyContent: "center",
                                                                    alignItems: "center",
                                                                    gap: 0.75,
                                                                    mb: 0.75,
                                                                }}
                                                            >
                                                                {getStatusIcon(s.key)}
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    fontWeight={800}
                                                                    sx={{ letterSpacing: "0.06em" }}
                                                                >
                                                                    {s.label.toUpperCase()}
                                                                </Typography>
                                                            </Box>
                                                            <Typography
                                                                variant="h4"
                                                                fontWeight={800}
                                                                sx={{ color: "text.primary", letterSpacing: "-0.02em" }}
                                                            >
                                                                {s.value.toLocaleString()}
                                                            </Typography>
                                                            {p != null && (
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        display: "block",
                                                                        mt: 0.5,
                                                                        color: "text.secondary",
                                                                        fontWeight: 700,
                                                                    }}
                                                                >
                                                                    {p.toFixed(2)}%
                                                                </Typography>
                                                            )}
                                                        </Paper>
                                                    </Grid>
                                                );
                                            })}
                                    </Grid>

                                    <Divider sx={{ my: 2 }} />

                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2.5,
                                            textAlign: "center",
                                            borderRadius: 2,
                                            border: `1px solid ${WB_UI.color.border}`,
                                            backgroundColor: "#fff",
                                        }}
                                    >
                                        <Typography variant="body2" color="text.secondary" gutterBottom fontWeight={600}>
                                            Total Messages
                                        </Typography>
                                        <Typography
                                            variant="h4"
                                            fontWeight={800}
                                            sx={{ color: WB_UI.color.primaryButtonBg }}
                                        >
                                            {statusData.total_messages}
                                        </Typography>
                                    </Paper>
                                </>
                            );
                        })()}
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: 200,
                        }}
                    >
                        <Typography variant="body1" color="text.secondary">
                            No status data available
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, borderTop: `1px solid ${WB_UI.color.border}` }}>
                <Button onClick={onClose} variant="contained" sx={WB_SX.primaryButton}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CampaignStatusModal;
